import { Injectable } from '@angular/core';
import { _padStart } from '@core/lodash/lodash';
import { ShowSavedSuccessToast } from '@core/toaster/toaster-decorators';
import { ToasterService } from '@core/toaster/toaster.service';
import { PaymentTransactionModality } from '@gandalf/constants';
import { PracticePayCreatePaymentGroupRequest } from '@gandalf/model/practice-pay-create-payment-group-request';
import { PracticePayCreatePaymentTransactionRequest } from '@gandalf/model/practice-pay-create-payment-transaction-request';
import { PracticePayDeleteStoredPaymentMethodRequest } from '@gandalf/model/practice-pay-delete-stored-payment-method-request';
import { PracticePayGetStoredPaymentMethodsRequest } from '@gandalf/model/practice-pay-get-stored-payment-methods-request';
import { PracticePayInitializePaymentSessionRequest } from '@gandalf/model/practice-pay-initialize-payment-session-request';
import { PracticePayInitializeStoreCardSessionRequest } from '@gandalf/model/practice-pay-initialize-store-card-session-request';
import { PracticePayInitializeTerminalSessionRequest } from '@gandalf/model/practice-pay-initialize-terminal-session-request';
import { PracticePayPaymentTransactionResponse } from '@gandalf/model/practice-pay-payment-transaction-response';
import { PracticePayRefundTransactionResponse } from '@gandalf/model/practice-pay-refund-transaction-response';
import { PracticePayStoredPaymentMethodResponse } from '@gandalf/model/practice-pay-stored-payment-method-response';
import { PracticePayGandalfService } from '@gandalf/services';
import dayjs from 'dayjs';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { InvoicesUpdatedResponse } from '@gandalf/model/invoices-updated-response';

export interface PracticePayStoredPaymentMethodForTable extends PracticePayStoredPaymentMethodResponse {
	expirationDateString?: string;
	cardTypeString?: string;
	expired: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class PracticePayPaymentService {

	constructor(
		private practicePayGandalfService: PracticePayGandalfService,
		private toasterService: ToasterService,
	) {
	}

	/* istanbul ignore next: gandalf */
	findSavedPaymentCardsByPersonId(request: PracticePayGetStoredPaymentMethodsRequest): Observable<PracticePayStoredPaymentMethodResponse[]> {
		return this.practicePayGandalfService.getStoredPaymentMethods(request);
	}

	findSavedPaymentCardsByPersonIdForTable(request: PracticePayGetStoredPaymentMethodsRequest): Observable<PracticePayStoredPaymentMethodForTable[]> {
		return this.findSavedPaymentCardsByPersonId(request).pipe(
			map(tokens => tokens.map(this.formatPaymentCardInformationForDisplay)),
		);
	}

	private formatPaymentCardInformationForDisplay = (token: PracticePayStoredPaymentMethodResponse): PracticePayStoredPaymentMethodForTable => {
		const tokenForTable: PracticePayStoredPaymentMethodForTable = token as PracticePayStoredPaymentMethodForTable;
		tokenForTable.cardTypeString = `${this.formatCardBrand(token.brand)} ending in ${token.lastFour}`;

		// Parse expiration - handle both separate month/year fields and combined MM/YY format
		const {month, year} = this.parseExpiration(token.expiryMonth, token.expiryYear);
		tokenForTable.expired = this.cardIsExpiredFromParsed(month, year);

		const displayMonth = _padStart(month ?? '', 2, '0');
		const displayYear = year ?? '';
		tokenForTable.expirationDateString = (`${tokenForTable.expired ? 'Expired ' : ''}${displayMonth}/${displayYear}`).trim();
		return tokenForTable;
	};

	private formatCardBrand(brand: string | null): string {
		if (!brand) {
			return 'Card';
		}
		// Capitalize first letter of each word
		return brand.split(' ').map(word =>
			word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
		).join(' ');
	}

	/**
	 * Parses expiration from either separate month/year fields or combined MM/YY format
	 */
	parseExpiration(expiryMonth: string | null, expiryYear: string | null): { month: string | null, year: string | null } {
		// Check if expiryMonth contains MM/YY format (e.g., "12/25" or "12/2025")
		if (expiryMonth && expiryMonth.includes('/')) {
			const parts = expiryMonth.split('/');
			if (parts.length === 2) {
				let yearPart = parts[1].trim();
				// Convert 2-digit year to 4-digit (e.g., "25" -> "2025")
				if (yearPart.length === 2) {
					yearPart = `20${yearPart}`;
				}
				return {month: parts[0].trim(), year: yearPart};
			}
		}
		// Check if expiryYear contains MM/YY format
		if (expiryYear && expiryYear.includes('/')) {
			const parts = expiryYear.split('/');
			if (parts.length === 2) {
				let yearPart = parts[1].trim();
				if (yearPart.length === 2) {
					yearPart = `20${yearPart}`;
				}
				return {month: parts[0].trim(), year: yearPart};
			}
		}
		// Standard separate fields - convert 2-digit year to 4-digit if needed
		let year = expiryYear;
		if (year && year.length === 2) {
			year = `20${year}`;
		}
		return {month: expiryMonth, year};
	}

	/**
	 * Checks if card is expired using pre-parsed month and year values
	 */
	cardIsExpiredFromParsed(month: string | null, year: string | null): boolean {
		if (!month || !year) {
			return false;
		}
		const monthNum = parseInt(month, 10);
		const yearNum = parseInt(year, 10);
		if (isNaN(monthNum) || isNaN(yearNum)) {
			return false;
		}
		const expirationDate = dayjs(new Date(yearNum, monthNum - 1, 1)).endOf('month');
		return dayjs().isAfter(expirationDate);
	}

	cardIsExpired(expirationMonth: string | null, expirationYear: string | null): boolean {
		const {month, year} = this.parseExpiration(expirationMonth, expirationYear);
		return this.cardIsExpiredFromParsed(month, year);
	}

	/* istanbul ignore next: gandalf */
	removeStoredPaymentMethod(request: PracticePayDeleteStoredPaymentMethodRequest): Observable<void> {
		return this.practicePayGandalfService.deleteStoredPaymentMethod(request);
	}

	/* istanbul ignore next: gandalf */
	initializeStoreCardSession(request: PracticePayInitializeStoreCardSessionRequest): Observable<string> {
		return this.practicePayGandalfService.initializeStoreCardSession(request);
	}

	/* istanbul ignore next: gandalf */
	initializePaymentSession(request: PracticePayInitializePaymentSessionRequest): Observable<string> {
		return this.practicePayGandalfService.initializePaymentSession(request);
	}

	/* istanbul ignore next: gandalf */
	initializeTerminalSession(request: PracticePayInitializeTerminalSessionRequest): Observable<string> {
		return this.practicePayGandalfService.initializeTerminalSession(request);
	}

	/* istanbul ignore next: gandalf */
	createPaymentTransaction(request: PracticePayCreatePaymentTransactionRequest): Observable<PracticePayPaymentTransactionResponse> {
		return this.practicePayGandalfService.createPaymentTransaction(request);
	}

	/* istanbul ignore next: gandalf */
	@ShowSavedSuccessToast()
	createPaymentGroup(request: PracticePayCreatePaymentGroupRequest): Observable<InvoicesUpdatedResponse[]> {
		return this.practicePayGandalfService.createPaymentGroup(request);
	}

	createInsurancePaymentGroup(request: PracticePayCreatePaymentGroupRequest): Observable<InvoicesUpdatedResponse[]> {
		return this.practicePayGandalfService.createInsurancePaymentGroup(request).pipe(
			tap(() => this.toasterService.showSavedSuccess({
				title: 'Payment Processing',
				content: 'Your payment is processing. When your payment is finished processing, you will receive a message in your Inbox with the payment status.',
				timeOut: 0,
			})),
		);
	}

	getTransactionModalityFromCardBrand(cardBrand: string): PaymentTransactionModality {
		const isDebitCard = cardBrand?.toLowerCase().includes('debit');
		return isDebitCard ? PaymentTransactionModality.DEBIT : PaymentTransactionModality.CREDIT;
	}

	/* istanbul ignore next: gandalf */
	existsActivePracticePayPaymentByInvoice(invoiceId: number): Observable<boolean> {
		return this.practicePayGandalfService.existsActivePracticePayPaymentByInvoice(invoiceId);
	}

	/* istanbul ignore next: gandalf */
	existsPracticePaySaleTransaction(paymentId: number): Observable<boolean> {
		return this.practicePayGandalfService.existsPracticePaySaleTransaction(paymentId);
	}

	/* istanbul ignore next: gandalf */
	refundSaleTransaction(paymentId: number): Observable<PracticePayRefundTransactionResponse> {
		return this.practicePayGandalfService.refundPayment(paymentId);
	}
}
